import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import {
  ConsignmentItemFormValues,
  PackagingType,
} from 'features/declaration/consignment-item/models/schema'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankPackaging } from 'features/declaration/models/blankItems'
import { useCalculatorModal } from 'features/declaration/form/components/calculator/CalculatorModalContext'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import FormField from '../../../../components/p5/FormField'
import DeleteIcon from '../../../../components/icons/DeleteIcon'
import FormSelect from '../../../../components/p5/FormSelect'
import { getSelectCountryFilter } from '../../../../components/react-select/CountryFunctions'
import Input from '../../../../components/p5/Input'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'

const scope = 'packaging'

function Packaging() {
  const { t } = useTranslation()

  const { control } = useFormContext<ConsignmentItemFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<ConsignmentItemFormValues, PackagingType>({
    control,
    name: scope,
  })

  const { setValue } = useFormContext()
  const [, packagesOptions] = useCodelist('PACKAGES')

  const { open } = useCalculatorModal()

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfPackages`),
      ...visibleFields.map((f) => `${scope}.${f.index}.numberOfPackages`),
      ...visibleFields.map((f) => `${scope}.${f.index}.shippingMarks`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-packages"
        headingLabel={t('declaration.p5.packages')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const packages = fieldWithIndex.field
          if (packages.deleted) return null
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const
          return (
            <div className="packaging-row" key={`packaging${packages.id}`}>
              <div className="packaging-row__header">
                <span className="packaging-row__no">
                  {t('declaration.p5.sequenceNumber')}
                  {packages.sequenceNumber + 1}
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-trash"
                  onClick={() => softRemove(fieldWithIndex.index)}
                  title={t('buttons.remove')}
                >
                  <DeleteIcon />
                </button>
              </div>
              <FormField
                labelKey="declaration.p5.typeOfPackages"
                fieldRule={fieldRequirements[`${innerItemScope}.typeOfPackages`]}
              >
                <FormSelect
                  field={`${innerItemScope}.typeOfPackages`}
                  labelKey="declaration.p5.typeOfPackages"
                  options={packagesOptions}
                  customFilterOption={getSelectCountryFilter}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfPackages`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.numberOfPackages"
                fieldRule={fieldRequirements[`${innerItemScope}.numberOfPackages`]}
              >
                <div className="input-group">
                  <Input
                    <ConsignmentItemFormValues>
                    type="number"
                    field={`${innerItemScope}.numberOfPackages`}
                    labelKey="declaration.p5.numberOfPackages"
                    min={0}
                    placeholder={t('declaration.p5.numberOfPackages')}
                    fieldRule={fieldRequirements[`${innerItemScope}.numberOfPackages`]}
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-light h-100 border fw-bold"
                      onClick={() => {
                        open({
                          onResolve: (value: number) => {
                            setValue(`${innerItemScope}.numberOfPackages`, value)
                          },
                        })
                      }}
                    >
                      <i className="fas fa-calculator" />
                    </button>
                  </div>
                </div>
              </FormField>
              <FormField
                className="packaging-row__shipping"
                labelKey="declaration.p5.shippingMarks"
                fieldRule={fieldRequirements[`${innerItemScope}.shippingMarks`]}
              >
                <Input
                  <ConsignmentItemFormValues>
                  type="textarea"
                  field={`${innerItemScope}.shippingMarks`}
                  labelKey="declaration.p5.shippingMarks"
                  placeholder={t('declaration.p5.shippingMarks')}
                  fieldRule={fieldRequirements[`${innerItemScope}.shippingMarks`]}
                />
              </FormField>
            </div>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankPackaging)}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewPackage')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}
export default Packaging
